import { useSearchParams } from 'react-router';

/** The param naming the open dialog. One dialog is open per URL, app-wide. */
export const URL_DIALOG_PARAM = 'dialog';

export type UrlDialogIdentity = Record<string, string | number>;

export interface UrlDialog {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

export interface UrlDialogTarget<TKey extends string> {
  params: Record<TKey, string>;
  close: () => void;
}

function clear(params: URLSearchParams, keys: string[]): void {
  params.delete(URL_DIALOG_PARAM);
  keys.forEach((key) => params.delete(key));
}

/**
 * A dialog whose openness lives in the query string, written from the row side.
 *
 * `dialog=<name>` says which dialog and `identity` pins which instance of it, so
 * pasting the link back opens what the sender was looking at. Opening pushes a
 * history entry and closing replaces it, so Back closes an open dialog and a
 * shut one stays shut.
 */
export function useUrlDialog(name: string, identity: UrlDialogIdentity = {}): UrlDialog {
  const [searchParams, setSearchParams] = useSearchParams();
  const entries = Object.entries(identity);

  const isOpen = searchParams.get(URL_DIALOG_PARAM) === name && entries.every(([key, value]) => searchParams.get(key) === String(value));

  const setOpen = (open: boolean) => {
    setSearchParams(
      (prev) => {
        if (open) {
          prev.set(URL_DIALOG_PARAM, name);
          entries.forEach(([key, value]) => prev.set(key, String(value)));
        } else {
          clear(
            prev,
            entries.map(([key]) => key)
          );
        }
        return prev;
      },
      { replace: !open }
    );
  };

  return { isOpen, setOpen };
}

/**
 * The record the URL asks this dialog to show, or nothing when it asks for none.
 *
 * `keys` are the identity params the dialog needs; a URL missing any of them
 * names no record.
 */
export function useUrlDialogTarget<TKey extends string>(name: string, keys: TKey[]): UrlDialogTarget<TKey> | undefined {
  const [searchParams, setSearchParams] = useSearchParams();

  const close = () =>
    setSearchParams(
      (prev) => {
        clear(prev, keys);
        return prev;
      },
      { replace: true }
    );

  if (searchParams.get(URL_DIALOG_PARAM) !== name) return undefined;

  const params = {} as Record<TKey, string>;
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value === null) return undefined;
    params[key] = value;
  }

  return { params, close };
}
